import PageManager from './page-manager';
import { bind, debounce } from 'lodash';
import checkIsGiftCertValid from './common/gift-certificate-validator';
import { createTranslationDictionary } from './common/utils/translations-utils';
import utils from '@bigcommerce/stencil-utils';
import ShippingEstimator from './cart/shipping-estimator';
import { defaultModal, ModalEvents, showAlertModal } from './global/modal';
import swal from './global/sweet-alert';
import CartItemDetails from './common/cart-item-details';

export default class Cart extends PageManager {
    onReady() {
        this.$modal = null;
        this.$cartPageContent = $('[data-cart]');
        this.$cartContent = $('[data-cart-content]');
        this.$cartMessages = $('[data-cart-status]');
        this.$cartTotals = $('[data-cart-totals]');
        this.$overlay = $('[data-cart] .loadingOverlay')
            .hide(); // TODO: temporary until roper pulls in his cart components
        this.$activeCartItemId = null;
        this.$activeCartItemBtnAction = null;

        this.extraProductInfo();
        this.setApplePaySupport();
        this.bindEvents();
    }

    setApplePaySupport() {
        if (window.ApplePaySession) {
            this.$cartPageContent.addClass('apple-pay-supported');
        }
    }

    alertCanada() {
        const message = '<p>It looks like you’re shopping with us from Canada. You’ve added an item or items to your cart that are not available for processing and shipment to Canadian addresses. Please review your cart and remove these items.</p>\
            <p>If you are not having your order shipped to a Canadian Address, please disregard this message, and proceed with your order.</p>';
        showAlertModal(message);
    }

    cartUpdate($target) {
        const itemId = $target.data('cartItemid');
        this.$activeCartItemId = itemId;
        this.$activeCartItemBtnAction = $target.data('action');

        const $el = $(`#qty-${itemId}`);
        const oldQty = parseInt($el.val(), 10);
        const maxQty = parseInt($el.data('quantityMax'), 10);
        const minQty = parseInt($el.data('quantityMin'), 10);
        const minError = $el.data('quantityMinError');
        const maxError = $el.data('quantityMaxError');
        const newQty = $target.data('action') === 'inc' ? oldQty + 1 : oldQty - 1;
        // Does not quality for min/max quantity
        if (newQty < minQty) {
            return swal.fire({
                text: minError,
                icon: 'error',
            });
        } else if (maxQty > 0 && newQty > maxQty) {
            return swal.fire({
                text: maxError,
                icon: 'error',
            });
        }

        this.$overlay.show();

        utils.api.cart.itemUpdate(itemId, newQty, (err, response) => {
            this.$overlay.hide();

            if (response.data.status === 'succeed') {
                // if the quantity is changed "1" from "0", we have to remove the row.
                const remove = (newQty === 0);

                this.refreshContent(remove);
            } else {
                $el.val(oldQty);
                swal.fire({
                    text: response.data.errors.join('\n'),
                    icon: 'error',
                });
            }
        });
    }

    cartUpdateQtyTextChange($target, preVal = null) {
        const itemId = $target.data('cartItemid');
        const $el = $(`#qty-${itemId}`);
        const maxQty = parseInt($el.data('quantityMax'), 10);
        const minQty = parseInt($el.data('quantityMin'), 10);
        const oldQty = preVal !== null ? preVal : minQty;
        const minError = $el.data('quantityMinError');
        const maxError = $el.data('quantityMaxError');
        const newQty = parseInt(Number($el.val()), 10);
        let invalidEntry;

        // Does not quality for min/max quantity
        if (!newQty) {
            invalidEntry = $el.val();
            $el.val(oldQty);
            return swal.fire({
                text: this.context.invalidEntryMessage.replace('[ENTRY]', invalidEntry),
                icon: 'error',
            });
        } else if (newQty < minQty) {
            $el.val(oldQty);
            return swal.fire({
                text: minError,
                icon: 'error',
            });
        } else if (maxQty > 0 && newQty > maxQty) {
            $el.val(oldQty);
            return swal.fire({
                text: maxError,
                icon: 'error',
            });
        }

        this.$overlay.show();
        utils.api.cart.itemUpdate(itemId, newQty, (err, response) => {
            this.$overlay.hide();

            if (response.data.status === 'succeed') {
                // if the quantity is changed "1" from "0", we have to remove the row.
                const remove = (newQty === 0);

                this.refreshContent(remove);
            } else {
                $el.val(oldQty);
                swal.fire({
                    text: response.data.errors.join('\n'),
                    icon: 'error',
                });
            }
        });
    }

    cartRemoveItem(itemId) {
        this.$overlay.show();
        utils.api.cart.itemRemove(itemId, (err, response) => {
            if (response.data.status === 'succeed') {
                this.refreshContent(true);
            } else {
                swal.fire({
                    text: response.data.errors.join('\n'),
                    icon: 'error',
                });
            }
        });
    }

    cartEditOptions(itemId, productId) {
        const context = { productForChangeId: productId, ...this.context };
        const modal = defaultModal();

        if (this.$modal === null) {
            this.$modal = $('#modal');
        }

        const options = {
            template: 'cart/modals/configure-product',
        };

        modal.open();
        this.$modal.find('.modal-content').addClass('hide-content');

        utils.api.productAttributes.configureInCart(itemId, options, (err, response) => {
            modal.updateContent(response.content);
            const optionChangeHandler = () => {
                const $productOptionsContainer = $('[data-product-attributes-wrapper]', this.$modal);
                const modalBodyReservedHeight = $productOptionsContainer.outerHeight();

                if ($productOptionsContainer.length && modalBodyReservedHeight) {
                    $productOptionsContainer.css('height', modalBodyReservedHeight);
                }
            };

            if (this.$modal.hasClass('open')) {
                optionChangeHandler();
            } else {
                this.$modal.one(ModalEvents.opened, optionChangeHandler);
            }

            this.productDetails = new CartItemDetails(this.$modal, context);

            this.bindGiftWrappingForm();
        });

        utils.hooks.on('product-option-change', (event, currentTarget) => {
            const $form = $(currentTarget).find('form');
            const $submit = $('input.button', $form);
            const $messageBox = $('.alertMessageBox');

            utils.api.productAttributes.optionChange(productId, $form.serialize(), (err, result) => {
                const data = result.data || {};

                if (err) {
                    swal.fire({
                        text: err,
                        icon: 'error',
                    });
                    return false;
                }

                if (data.purchasing_message) {
                    $('p.alertBox-message', $messageBox).text(data.purchasing_message);
                    $submit.prop('disabled', true);
                    $messageBox.show();
                } else {
                    $submit.prop('disabled', false);
                    $messageBox.hide();
                }

                if (!data.purchasable || !data.instock) {
                    $submit.prop('disabled', true);
                } else {
                    $submit.prop('disabled', false);
                }
            });
        });
    }

    refreshContent(remove) {
        const $cartItemsRows = $('[data-item-row]', this.$cartContent);
        const $cartPageTitle = $('[data-cart-page-title]');
        const options = {
            template: {
                content: 'cart/content',
                totals: 'cart/totals',
                pageTitle: 'cart/page-title',
                statusMessages: 'cart/status-messages',
            },
        };

        this.$overlay.show();

        // Remove last item from cart? Reload
        if (remove && $cartItemsRows.length === 1) {
            return window.location.reload();
        }

        utils.api.cart.getContent(options, async (err, response) => {
            this.$cartContent.html(response.content);
            this.$cartTotals.html(response.totals);
            this.$cartMessages.html(response.statusMessages);

            $cartPageTitle.replaceWith(response.pageTitle);
            await this.extraProductInfo();
            this.bindEvents();
            this.$overlay.hide();

            const quantity = $('[data-cart-quantity]', this.$cartContent).data('cartQuantity') || 0;

            $('body').trigger('cart-quantity-update', quantity);

            $(`[data-cart-itemid='${this.$activeCartItemId}']`, this.$cartContent)
                .filter(`[data-action='${this.$activeCartItemBtnAction}']`)
                .trigger('focus');

            this.updateCartNotice(response.content);
        });
    }

    bindCartEvents() {
        const debounceTimeout = 400;
        const cartUpdate = bind(debounce(this.cartUpdate, debounceTimeout), this);
        const cartUpdateQtyTextChange = bind(debounce(this.cartUpdateQtyTextChange, debounceTimeout), this);
        const cartRemoveItem = bind(debounce(this.cartRemoveItem, debounceTimeout), this);
        let preVal;

        // cart update
        $('[data-cart-update]', this.$cartContent).on('click', event => {
            const $target = $(event.currentTarget);

            event.preventDefault();

            // update cart quantity
            cartUpdate($target);
        });

        // cart qty manually updates
        $('.cart-item-qty-input', this.$cartContent).on('focus', function onQtyFocus() {
            preVal = this.value;
        }).change(event => {
            const $target = $(event.currentTarget);
            event.preventDefault();

            // update cart quantity
            cartUpdateQtyTextChange($target, preVal);
        });

        $('.cart-remove', this.$cartContent).on('click', event => {
            const itemId = $(event.currentTarget).data('cartItemid');
            const string = $(event.currentTarget).data('confirmDelete');
            swal.fire({
                text: string,
                icon: 'warning',
                showCancelButton: true,
                cancelButtonText: this.context.cancelButtonText,
            }).then((result) => {
                if (result.value) {
                    // remove item from cart
                    cartRemoveItem(itemId);
                }
            });
            event.preventDefault();
        });

        $('[data-item-edit]', this.$cartContent).on('click', event => {
            const itemId = $(event.currentTarget).data('itemEdit');
            const productId = $(event.currentTarget).data('productId');
            event.preventDefault();
            // edit item in cart
            this.cartEditOptions(itemId, productId);
        });

        $('#clear-cart').on('click', () => {
            this.$overlay.show();

            fetch('/api/storefront/carts/' + this.context.cartId, {
                credentials: 'same-origin',
                method: 'DELETE'
            }).then(function(response) {
                return window.location.reload();
            });
        })
    }

    bindPromoCodeEvents() {
        const $couponContainer = $('.coupon-code');
        const $couponForm = $('.coupon-form');
        const $codeInput = $('[name="couponcode"]', $couponForm);

        $('.coupon-code-add').on('click', event => {
            event.preventDefault();

            $(event.currentTarget).hide();
            $couponContainer.show();
            $('.coupon-code-cancel').show();
            $codeInput.trigger('focus');
        });

        $('.coupon-code-cancel').on('click', event => {
            event.preventDefault();

            $couponContainer.hide();
            $('.coupon-code-cancel').hide();
            $('.coupon-code-add').show();
        });

        $couponForm.on('submit', event => {
            const code = $codeInput.val();

            event.preventDefault();

            // Empty code
            if (!code) {
                return swal.fire({
                    text: $codeInput.data('error'),
                    icon: 'error',
                });
            }

            utils.api.cart.applyCode(code, (err, response) => {
                if (response.data.status === 'success') {
                    this.refreshContent();
                } else {
                    swal.fire({
                        html: response.data.errors.join('\n'),
                        icon: 'error',
                    });
                }
            });
        });
    }

    bindGiftCertificateEvents() {
        const $certContainer = $('.gift-certificate-code');
        const $certForm = $('.cart-gift-certificate-form');
        const $certInput = $('[name="certcode"]', $certForm);

        $('.gift-certificate-add').on('click', event => {
            event.preventDefault();
            $(event.currentTarget).toggle();
            $certContainer.toggle();
            $('.gift-certificate-cancel').toggle();
        });

        $('.gift-certificate-cancel').on('click', event => {
            event.preventDefault();
            $certContainer.toggle();
            $('.gift-certificate-add').toggle();
            $('.gift-certificate-cancel').toggle();
        });

        $certForm.on('submit', event => {
            const code = $certInput.val();

            event.preventDefault();

            if (!checkIsGiftCertValid(code)) {
                const validationDictionary = createTranslationDictionary(this.context);
                return swal.fire({
                    text: validationDictionary.invalid_gift_certificate,
                    icon: 'error',
                });
            }

            utils.api.cart.applyGiftCertificate(code, (err, resp) => {
                if (resp.data.status === 'success') {
                    this.refreshContent();
                } else {
                    swal.fire({
                        html: resp.data.errors.join('\n'),
                        icon: 'error',
                    });
                }
            });
        });
    }

    bindGiftWrappingEvents() {
        const modal = defaultModal();

        $('[data-item-giftwrap]').on('click', event => {
            const itemId = $(event.currentTarget).data('itemGiftwrap');
            const options = {
                template: 'cart/modals/gift-wrapping-form',
            };

            event.preventDefault();

            modal.open();

            utils.api.cart.getItemGiftWrappingOptions(itemId, options, (err, response) => {
                modal.updateContent(response.content);

                this.bindGiftWrappingForm();
            });
        });
    }

    bindGiftWrappingForm() {
        $('.giftWrapping-select').on('change', event => {
            const $select = $(event.currentTarget);
            const id = $select.val();
            const index = $select.data('index');

            if (!id) {
                return;
            }

            const allowMessage = $select.find(`option[value=${id}]`).data('allowMessage');

            $(`.giftWrapping-image-${index}`).hide();
            $(`#giftWrapping-image-${index}-${id}`).show();

            if (allowMessage) {
                $(`#giftWrapping-message-${index}`).show();
            } else {
                $(`#giftWrapping-message-${index}`).hide();
            }
        });

        $('.giftWrapping-select').trigger('change');

        function toggleViews() {
            const value = $('input:radio[name ="giftwraptype"]:checked').val();
            const $singleForm = $('.giftWrapping-single');
            const $multiForm = $('.giftWrapping-multiple');

            if (value === 'same') {
                $singleForm.show();
                $multiForm.hide();
            } else {
                $singleForm.hide();
                $multiForm.show();
            }
        }

        $('[name="giftwraptype"]').on('click', toggleViews);

        toggleViews();
    }

    bindEvents() {
        this.bindCartEvents();
        this.bindPromoCodeEvents();
        this.bindGiftWrappingEvents();
        this.bindGiftCertificateEvents();

        // initiate shipping estimator module
        const shippingErrorMessages = {
            country: this.context.shippingCountryErrorMessage,
            province: this.context.shippingProvinceErrorMessage,
        };
        this.shippingEstimator = new ShippingEstimator($('[data-shipping-estimator]'), shippingErrorMessages);
    }

    updateCartNotice(content) {
        let total = 0;
        const $notice = $('.cart-notice.cart-shipping');
        const parser = new DOMParser();
        const cartTable = parser.parseFromString(content, 'text/html');

        const $row = $(cartTable).find('[data-item-row]');
        $row.each((index, element) => {
            const rowTotal = $(element).find('.cart-item-actions .cart-item-value').data('value');
            
            total += +rowTotal;
        })

        // free shipping over $50
        if (total >= 50) {
            $notice.empty().html('<div class="notice-wording free-shipping">\
                <span class="icon"><svg><use xlink:href="#icon-cargo-truck-2" /></svg></span>\
                <span>CONGRATS! THIS ORDER QUALIFIES FOR FREE STANDARD SHIPPING! <a class="free-shipping-link" data-reveal-id="top_promotion">(View Details)</a></span>\
            </div>');
        } else {
            let toFreeShippingAmount = parseFloat(50 - total).toFixed(2);
            let progressBarWidth = parseFloat((total / 50) * 100).toFixed(2);
            $notice.empty().html(`<div class="notice-wording">\
                <span>You're <span class="to_free_shipping">${toFreeShippingAmount}</span> away from</span>\
                <span>FREE SHIPPING!</span>\
            </div>\
            <div class="notice-process-bar">\
                <span id="current-percent" style="width: ${progressBarWidth}%"></span>\
            </div>`);
        }
    }

    productWarningToggle() {
        $('.cart-item-warnings-show-all').click(function(e) {
            e.preventDefault();
            const $wrapper = $(e.currentTarget).parents('.cart-item-warnings-wrapper');
            const modal = defaultModal();
            modal.open();
            modal.$modal.addClass('overflow-auto');
            modal.$modal.one(ModalEvents.close, () => {
                modal.$modal.removeClass('overflow-auto');
            })
            const $includeWrapper = $wrapper.addClass('cart-item-warnings-modal-content').wrap( "<div></div>" ).parent();
            modal.updateContent($includeWrapper.html());
            $('.cart-item-warnings-title', modal.$content).removeClass('no-display');
        })
    }

    async extraProductInfo() {
        const $cartItemsRows = $('[data-item-row]', this.$cartContent);
        const fetchContent = [];
        if ($cartItemsRows.length > 0) {
            $cartItemsRows.each((index, item) => {
                const productId = $(item).data('product-id');

                fetchContent.push(
                    fetch('/graphql', {
                        method: 'POST',
                        headers: {
                         'Content-Type': 'application/json',
                         'Authorization': 'Bearer ' + this.context.settings.storefront_api.token
                        },
                        body: JSON.stringify({
                            query: 
                            `query VariantById {
                              site {
                                product(entityId: ${productId}) {
                                  name
                                  sku
                                  warranty
                                  prices {
                                    price {
                                      ...PriceFields
                                    }
                                    basePrice {
                                      ...PriceFields
                                    }
                                    salePrice {
                                      ...PriceFields
                                    }
                                    retailPrice {
                                      ...PriceFields
                                    }
                                  }
                                }
                              }
                            }
                            fragment PriceFields on Money {
                              value
                              currencyCode
                            }
                            `
                        })
                    })
                    .then(res => res.json())
                    .then(json => {
                        const product = json.data.site.product;
                        if (product) {
                            const base = product.prices.basePrice.value;
                            const warranty = product.warranty;
                            if (product.prices.salePrice) {
                                const cartPrice = product.prices.price.value;
                                const salePrice = product.prices.salePrice.value;
                                if (cartPrice == salePrice && cartPrice < base) {
                                    $(item).find('.cart-item-info .base-price').text(`$${base.toFixed(2)}`);
                                    let savedAmount = (base - cartPrice).toFixed(2);
                                    $(item).find('.cart-item-info .saved-message').text(`(You save $${savedAmount})`);
                                }
                            }
                            if (warranty) {
                                const jsonArray = JSON.parse(warranty);
                                const warnings = jsonArray.productWarnings;

                                if ("notAvailableInCanada" in jsonArray) {
                                    // product is disabled in canada
                                    if (jsonArray["notAvailableInCanada"] == true) {
                                        $(item).find('.cart-item-sku').after('<div class="not-available-canada">Not available in Canada</div>');
                                        if (this.context.currency_selector.active_currency_code == "CAD") {
                                            this.alertCanada();
                                        }
                                    }
                                }
                                if (warnings) {
                                    let $ul = $("<ul>", {"class": "warning-list"});
                                    for (let warning of warnings) {
                                        // let warningIcon = '<i class="icon"><svg><use xlink:href="#icon-warning" /></svg></i>';
                                        // let $li = $("<li>", {"class": "warning"})
                                        //     .append(warningIcon)
                                        //     .append($("<span>").append($("<span>", {"class": "warning-label"}).text(`Warning: ${warning.warningId}`))
                                        //     .append(`- ${warning.warningContent}`));
                                        let $li = $("<li>", {"class": "warning"}).append(`${warning.warningContent}`);
                                        $ul.append($li);
                                    }
                                    let showAllButton = `<a class="cart-item-warnings-show-all">\
                                        <span>Show All (${warnings.length})</span>\
                                    </a>`;
                                    $(item).find('.cart-item-warnings-title').text(`Product Warnings (${warnings.length})`);
                                    $(item).find('.cart-item-warnings').append($ul).append(showAllButton).removeClass('no-display');
                                }
                                this.productWarningToggle();
                            }
                        }
                    })
                )
            })

            return await Promise.all(fetchContent);
        }
    }
}
